import React, { useState, useEffect } from 'react';
import './App.css';
import { Game, LaunchRequest } from './types';
import { apiService } from './api';
import Header from './components/Header';
import Login from './components/Login';
import GameCard from './components/GameCard';
import LoadingSpinner from './components/LoadingSpinner';
import GameStatsPage from './components/GameStatsPage';
import GameDetailStatsPage from './components/GameDetailStatsPage';
import GameUserStatsPage from './components/GameUserStatsPage';
import CreateGamePage from './components/CreateGamePage';
import { LanguageCode } from './components/LanguageSelector';
import { CurrencyCode } from './components/CurrencySelector';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [games, setGames] = useState<Game[]>([]);
  const [filteredGames, setFilteredGames] = useState<Game[]>([]);
  const [balance, setBalance] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>('en');
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyCode>('USD');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [gameUrl, setGameUrl] = useState<string>('');
  const [showGame, setShowGame] = useState<boolean>(false);
  const [showStats, setShowStats] = useState<boolean>(false);
  const [showGameDetailStats, setShowGameDetailStats] = useState<boolean>(false);
  const [showGameUserStats, setShowGameUserStats] = useState<boolean>(false);
  const [showCreateGame, setShowCreateGame] = useState<boolean>(false);
  const [selectedGameId, setSelectedGameId] = useState<number | null>(null);
  const [selectedGameName, setSelectedGameName] = useState<string>('');

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        setLoading(true);
        // Get user info first to check auth and get admin status
        const userResponse = await apiService.getUser();
        if (userResponse.success) {
          setIsAuthenticated(true);
          setIsAdmin(userResponse.isAdmin || false);
          if (userResponse.currency) {
            setSelectedCurrency(userResponse.currency);
          }
        } else {
          setIsAuthenticated(false);
          return;
        }
        // Get balance
        const balanceResponse = await apiService.getBalance();
        setBalance(balanceResponse.balance);
        await loadGames();
      } catch (err: any) {
        setIsAuthenticated(false);
        setIsAdmin(false);
        if (err.response?.status !== 401) {
          setError('Failed to check authentication status.');
          console.error('Auth check error:', err);
        }
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter games based on search term
  useEffect(() => {
    const filtered = games.filter(game =>
      game.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      game.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredGames(filtered);
  }, [games, searchTerm]);

  // Restore state from URL hash on mount and handle browser back/forward
  useEffect(() => {
    const restoreStateFromHash = () => {
      const hash = window.location.hash;
      
      // Reset all states first
      setShowStats(false);
      setShowGameDetailStats(false);
      setShowGameUserStats(false);
      setShowCreateGame(false);
      setSelectedGameId(null);
      setSelectedGameName('');

      // Only restore admin pages if user is admin
      if (!isAdmin) {
        // If not admin and hash points to admin page, redirect to home
        if (hash === '#stats' || hash === '#create-game' || hash.startsWith('#game/')) {
          window.history.pushState({ page: 'home' }, '', '#');
        }
        return;
      }

      if (hash === '#stats') {
        setShowStats(true);
      } else if (hash === '#create-game') {
        setShowCreateGame(true);
      } else if (hash.startsWith('#game/')) {
        const parts = hash.split('/');
        const gameId = parseInt(parts[1], 10);
        if (!isNaN(gameId)) {
          setSelectedGameId(gameId);
          
          // Try to find game name from loaded games list
          const game = games.find(g => g.id === gameId);
          if (game) {
            setSelectedGameName(game.name);
          } else {
            // If games not loaded yet, use placeholder (will be updated when games load)
            setSelectedGameName(`Game ${gameId}`);
          }
          
          if (parts[2] === 'users') {
            setShowGameUserStats(true);
          } else {
            setShowGameDetailStats(true);
          }
        }
      }
    };

    // Restore state on initial load
    restoreStateFromHash();

    // Handle browser back/forward buttons
    const handlePopState = () => {
      restoreStateFromHash();
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [games, isAdmin]);

  // Update game name when games are loaded and we have a selected game
  useEffect(() => {
    if (selectedGameId && games.length > 0) {
      const game = games.find(g => g.id === selectedGameId);
      if (game && game.name !== selectedGameName) {
        setSelectedGameName(game.name);
      }
    }
  }, [games, selectedGameId, selectedGameName]);

  const loadGames = async () => {
    try {
      const gamesResponse = await apiService.getGames();
      setGames(gamesResponse.games);
    } catch (err) {
      console.error('Failed to load games:', err);
    }
  };

  const handleLogin = async (username: string, password: string) => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.login({ username, password });
      if (response.success) {
        setIsAuthenticated(true);
        // Load saved currency from login response
        if (response.currency) {
          setSelectedCurrency(response.currency);
        }
        // Set admin flag from login response
        setIsAdmin(response.isAdmin || false);
        
        // Always redirect to home page after login and clear all page states
        setShowStats(false);
        setShowGameDetailStats(false);
        setShowGameUserStats(false);
        setShowCreateGame(false);
        setSelectedGameId(null);
        setSelectedGameName('');
        window.history.pushState({ page: 'home' }, '', '#');
        
        await initializeApp();
      } else {
        setError(response.error || 'Login failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      setLoading(true);
      await apiService.logout();
      setIsAuthenticated(false);
      setIsAdmin(false);
      setGames([]);
      setBalance(0);
      setError('');
    } catch (err) {
      console.error('Logout error:', err);
      // Still log out locally even if API call fails
      setIsAuthenticated(false);
      setIsAdmin(false);
      setGames([]);
      setBalance(0);
    } finally {
      setLoading(false);
    }
  };

  const initializeApp = async () => {
    try {
      setError('');
      const balanceResponse = await apiService.getBalance();
      setBalance(balanceResponse.balance);
      await loadGames();
    } catch (err) {
      setError('Failed to initialize app. Please check if the backend is running.');
      console.error('Initialization error:', err);
    }
  };

  const handleRefreshBalance = async () => {
    try {
      setLoading(true);
      const response = await apiService.getBalance();
      setBalance(response.balance);
    } catch (err) {
      setError('Failed to refresh balance');
      console.error('Balance refresh error:', err);
    } finally {
      setLoading(false);
    }
  };


  const handlePlayDemo = async (game: Game) => {
    try {
      setLoading(true);
      const request: LaunchRequest = {
        demo: true,
        currency: selectedCurrency,
        gameId: game.id,
        language: selectedLanguage,
        playerId: 'demo-player',
        token: 'demo-token',
        integratorId: ''
      };
      
      const response = await apiService.launchGame(request);
      if (response.success && response.url) {
        setGameUrl(response.url);
        setShowGame(true);
      } else {
        setError('Failed to launch demo game');
      }
    } catch (err) {
      setError('Failed to launch demo game');
      console.error('Demo launch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayRealViaUrl = async (game: Game) => {
    try {
      setLoading(true);
      const tokenResponse = await apiService.generateToken({ gameId: game.id });
      if (tokenResponse.success && tokenResponse.token) {
        const gameBaseUrl = process.env.REACT_APP_GAME_BASE_URL || 'https://test-integrate.spinbyspin.net';
        const gameUrl = `${gameBaseUrl}/game_open/${game.id}/staging?token=${tokenResponse.token}`;
        setGameUrl(gameUrl);
        setShowGame(true);
      } else {
        setError('Failed to generate token');
      }
    } catch (err) {
      setError('Failed to generate token');
      console.error('Token generation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayReal = async (game: Game) => {
    try {
      setLoading(true);
      const request: LaunchRequest = {
        demo: false,
        currency: selectedCurrency,
        gameId: game.id,
        language: selectedLanguage,
        playerId: 'real-player',
        token: 'real-token',
        integratorId: 'staging'
      };
      
      const response = await apiService.launchGame(request);
      if (response.success && response.url) {
        setGameUrl(response.url);
        setShowGame(true);
      } else {
        setError('Failed to launch real money game');
      }
    } catch (err) {
      setError('Failed to launch real money game');
      console.error('Real launch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseGame = () => {
    setShowGame(false);
    setGameUrl('');
  };

  if (!isAuthenticated) {
    return (
      <Login
        onLogin={handleLogin}
        loading={loading}
        error={error}
      />
    );
  }

  if (loading && games.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className={`App${showGame ? ' App--game-open' : ''}`}>
      {!showStats && !showGameDetailStats && !showGameUserStats && !showCreateGame && (
        <Header
          balance={balance}
          onRefreshBalance={handleRefreshBalance}
          onLogout={handleLogout}
          loading={loading}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          selectedLanguage={selectedLanguage}
          onLanguageChange={setSelectedLanguage}
          selectedCurrency={selectedCurrency}
          onCurrencyChange={async (currency) => {
            setSelectedCurrency(currency);
            // Save currency preference to backend
            try {
              await apiService.saveCurrency(currency);
            } catch (err) {
              console.error('Failed to save currency preference:', err);
              // Don't show error to user, just log it
            }
          }}
          isAdmin={isAdmin}
          onShowStats={() => {
            setShowStats(true);
            window.history.pushState({ page: 'stats' }, '', '#stats');
          }}
          onShowCreateGame={() => {
            setShowCreateGame(true);
            window.history.pushState({ page: 'create-game' }, '', '#create-game');
          }}
        />
      )}
      
      {showCreateGame ? (
        <CreateGamePage
          onBack={() => {
            setShowCreateGame(false);
            window.history.pushState({ page: 'home' }, '', '#');
          }}
          onSuccess={() => {
            setShowCreateGame(false);
            loadGames();
            window.history.pushState({ page: 'home' }, '', '#');
          }}
        />
      ) : showGameUserStats && selectedGameId ? (
        <GameUserStatsPage
          gameId={selectedGameId}
          gameName={selectedGameName}
          onBack={() => {
            setShowGameUserStats(false);
            setShowGameDetailStats(true);
            window.history.pushState({ page: 'game-detail', gameId: selectedGameId }, '', `#game/${selectedGameId}`);
          }}
        />
      ) : showGameDetailStats && selectedGameId ? (
        <GameDetailStatsPage
          gameId={selectedGameId}
          gameName={selectedGameName}
          isAdmin={isAdmin}
          onBack={() => {
            setShowGameDetailStats(false);
            setSelectedGameId(null);
            setSelectedGameName('');
            window.history.pushState({ page: 'stats' }, '', '#stats');
          }}
          onShowUserStats={(gameId: number, gameName: string) => {
            setSelectedGameId(gameId);
            setSelectedGameName(gameName);
            setShowGameUserStats(true);
            setShowGameDetailStats(false);
            window.history.pushState({ page: 'game-users', gameId }, '', `#game/${gameId}/users`);
          }}
        />
      ) : showStats ? (
        <GameStatsPage 
          onBack={() => {
            setShowStats(false);
            window.history.pushState({ page: 'home' }, '', '#');
          }}
          onGameClick={(gameId: number, gameName: string) => {
            setSelectedGameId(gameId);
            setSelectedGameName(gameName);
            setShowGameDetailStats(true);
            setShowStats(false);
            window.history.pushState({ page: 'game-detail', gameId }, '', `#game/${gameId}`);
          }}
        />
      ) : showGame ? (
        <div className="game-container">
          <div className="game-header">
            <button onClick={handleCloseGame} className="btn btn-close">← Back to Games</button>
          </div>
          <iframe
            src={gameUrl}
            className="game-iframe"
            title="Game"
            allowFullScreen
          />
        </div>
      ) : (
        <main className="main-content">
          {error && (
            <div className="error-message">
              <p>{error}</p>
              <button onClick={() => setError('')} className="btn btn-dismiss">×</button>
            </div>
          )}

          <div className="games-section">
            <h2>Available Games ({filteredGames.length})</h2>
            {filteredGames.length === 0 ? (
              <div className="no-games">
                <p>No games found matching your search.</p>
              </div>
            ) : (
              <div className="games-grid">
                {filteredGames.map(game => (
                  <GameCard
                    key={game.id}
                    game={game}
                    onPlayDemo={handlePlayDemo}
                    onPlayReal={handlePlayReal}
                    onPlayRealViaUrl={handlePlayRealViaUrl}
                    isAdmin={isAdmin}
          onShowStats={(gameId: number, gameName: string) => {
            setSelectedGameId(gameId);
            setSelectedGameName(gameName);
            setShowGameDetailStats(true);
            window.history.pushState({ page: 'game-detail', gameId }, '', `#game/${gameId}`);
          }}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      )}
    </div>
  );
}

export default App;